import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { ArticleService, ArticleHistoryItem } from '../../../services/article.service';
import { MatDialog } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { GenerateArticleDialogComponent } from '../../../dialogs/generate-article-dialog/generate-article-dialog.component';
import { ArticleViewDialogComponent } from '../../../dialogs/article-view-dialog/article-view-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-content-creation-article-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, MatAngularModule],
  templateUrl: './content-creation-article-tab.component.html',
  styleUrl: './content-creation-article-tab.component.scss'
})
export class ContentCreationArticleTabComponent implements OnInit, OnDestroy {
  commandBarInput = '';
  /** Toggle only — does not open a dialog */
  createNewSelected = true;
  historyItems: ArticleHistoryItem[] = [];
  historyTotal = 0;
  historyLoading = false;
  historyError: string | null = null;
  private subs: Subscription[] = [];

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private articleService: ArticleService,
    private dialog: MatDialog,
    private overlay: Overlay,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar
  ) {}

  get projectId(): string | null {
    return this.templateCommonService.getCurrentProjectContext()?.projectId ?? null;
  }

  /** Next / Enter allowed only when user entered a starting topic or title */
  get canProceedFromCommandBar(): boolean {
    return (this.commandBarInput || '').trim().length > 0;
  }

  ngOnInit(): void {
    this.loadHistory();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  loadHistory(): void {
    const pid = this.projectId;
    if (!pid) {
      this.historyItems = [];
      this.historyTotal = 0;
      this.cdr.markForCheck();
      return;
    }
    this.historyLoading = true;
    this.historyError = null;
    const sub = this.articleService.getHistory(pid, 0, 50).subscribe({
      next: (res) => {
        this.historyItems = res.items;
        this.historyTotal = res.total;
        this.historyLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.historyError = err.error?.detail || 'Failed to load article history';
        this.historyLoading = false;
        this.cdr.markForCheck();
      }
    });
    this.subs.push(sub);
  }

  toggleCreateNew(): void {
    this.createNewSelected = !this.createNewSelected;
    this.cdr.markForCheck();
  }

  /** Opens generate flow with command-bar text as article title; suggestions API uses same text for AI titles */
  openGenerateFromCommandBar(): void {
    if (!this.canProceedFromCommandBar) return;
    const pid = this.projectId;
    if (!pid) {
      this.snackBar.open('Select a project first.', 'Close', { duration: 4000 });
      return;
    }
    const articleTitle = (this.commandBarInput || '').trim();
    const ref = this.dialog.open(GenerateArticleDialogComponent, {
      width: '80%',
      maxWidth: '900px',
      maxHeight: '95vh',
      data: {
        projectId: pid,
        articleTitle,
        topic: articleTitle
      },
      disableClose: true,
      scrollStrategy: this.overlay.scrollStrategies.block()
    });
    this.commandBarInput = '';
    this.cdr.markForCheck();
    ref.afterClosed().subscribe((result: { refreshed?: boolean } | undefined) => {
      if (result?.refreshed) this.loadHistory();
      this.cdr.markForCheck();
    });
  }

  onCommandBarSubmit(): void {
    this.openGenerateFromCommandBar();
  }

  showArticle(item: ArticleHistoryItem): void {
    this.dialog.open(ArticleViewDialogComponent, {
      width: '80%',
      maxWidth: '900px',
      maxHeight: '95vh',
      data: { articleId: item.id },
      disableClose: true,
      scrollStrategy: this.overlay.scrollStrategies.block()
    });
  }

  downloadArticle(item: ArticleHistoryItem): void {
    this.articleService.export(item.id, 'doc').subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(item.title || 'article').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 60)}.doc`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => {}
    });
  }

  formatDate(iso: string): string {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { dateStyle: 'medium' });
    } catch {
      return iso;
    }
  }
}
